import React, { useEffect, useState } from "react";
import cx from "classnames";
import Form from 'react-bootstrap/Form';
import { useDispatch } from "react-redux";
import { change } from "redux-form";
import withStyles from "isomorphic-style-loader/withStyles";
import useQueryResponse from "../../../lib/hooks/useQueryResponse";
import getCategoryQuestions from './getCategoryQuestions.graphql';
import { readBookingDraft } from "../../../helpers/bookingDraft";
import bt from '../../commonStyle.css';

// M6 parity with mobile: render the category's questionnaire on the booking review page. Answers are
// kept as a { fieldKey: value } map and mirrored into the OrderDetails redux-form 'questionnaire'
// field as a JSON string, which createUserBooking forwards to Booking.questionnaireData (shown to the
// worker, M7). inputType is one of text | number | select | multiselect | boolean.
function QuestionnaireFields({ categoryId, orderId, formName = 'OrderDetails' }) {
    const dispatch = useDispatch();
    // Resumable booking: seed answers from the persisted draft (refresh / Back-into-/order / post-login
    // return) via a lazy initializer so the mirror-effect below never clobbers the rehydrated value with
    // an empty map on first render. The AI pre-fill effect (one-shot) still overrides this when present.
    const [answers, setAnswers] = useState(() => {
        try {
            const draft = readBookingDraft(orderId);
            if (draft.questionnaire) {
                const a = typeof draft.questionnaire === 'string' ? JSON.parse(draft.questionnaire) : draft.questionnaire;
                if (a && typeof a === 'object') return a;
            }
        } catch (e) { /* ignore */ }
        return {};
    });

    const { data } = useQueryResponse({
        query: getCategoryQuestions,
        variables: { categoryId: categoryId ? Number(categoryId) : null },
        fetchPolicy: 'cache-and-network',
        ssr: false,
    });

    const fields = data?.getCategoryQuestions?.results || [];

    useEffect(() => {
        // Keep the redux-form value in sync so the booking mutation can read it.
        dispatch(change(formName, 'questionnaire', Object.keys(answers).length ? JSON.stringify(answers) : null));
    }, [answers]);

    // One-shot AI pre-fill: if the "Describe your job" chat handed us answers for THIS category, apply
    // them once (then clear) so the customer lands on a form that's already filled in.
    useEffect(() => {
        if (typeof window === 'undefined' || !fields.length) return;
        try {
            const raw = window.sessionStorage.getItem('aiPrefill');
            if (!raw) return;
            const pf = JSON.parse(raw);
            if (String(pf.categoryId) !== String(categoryId)) return;
            const ans = typeof pf.answers === 'string' ? JSON.parse(pf.answers) : pf.answers;
            const keys = new Set(fields.map((f) => f.fieldKey));
            const valid = {};
            Object.keys(ans || {}).forEach((k) => { if (keys.has(k) && ans[k] != null) valid[k] = ans[k]; });
            // Seed the free-text "describe your job" field with the AI's job summary (user stays free to edit).
            const describe = typeof pf.describe === 'string' ? pf.describe.trim() : '';
            window.sessionStorage.removeItem('aiPrefill');
            if (describe) dispatch(change(formName, 'description', describe));
            if (Object.keys(valid).length) setAnswers(valid);
        } catch (e) { /* ignore */ }
    }, [categoryId, fields.length]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!fields.length) return null;

    const setAnswer = (key, value) => setAnswers(prev => ({ ...prev, [key]: value }));

    const parseOptions = (options) => {
        if (!options) return [];
        try { const o = JSON.parse(options); return Array.isArray(o) ? o : []; } catch (e) { return []; }
    };

    const toggleMulti = (key, opt) => {
        const cur = Array.isArray(answers[key]) ? answers[key] : [];
        setAnswer(key, cur.includes(opt) ? cur.filter(v => v !== opt) : [...cur, opt]);
    };

    return (
        <div>
            <hr className={bt.sectionDividerBorder} />
            <div className={cx(bt.commonViewBookingSubHeading, bt.space2)}>
                {/* Reuse a neutral label; the questions themselves carry the context. */}
                Additional details
            </div>
            {fields.map((f) => {
                const opts = parseOptions(f.options);
                return (
                    <Form.Group key={f.id} className={bt.space2}>
                        <label className={cx(bt.commonLabelText)}>
                            {f.label}{f.isRequired ? ' *' : ''}
                        </label>
                        {f.inputType === 'boolean' && (
                            <Form.Check
                                type="switch"
                                id={`q_${f.id}`}
                                checked={!!answers[f.fieldKey]}
                                onChange={(e) => setAnswer(f.fieldKey, e.target.checked)}
                                label={f.helpText || ''}
                            />
                        )}
                        {f.inputType === 'select' && (
                            <Form.Control as="select" value={answers[f.fieldKey] || ''} onChange={(e) => setAnswer(f.fieldKey, e.target.value)}>
                                <option value="">—</option>
                                {opts.map((o, i) => <option key={i} value={o}>{o}</option>)}
                            </Form.Control>
                        )}
                        {f.inputType === 'multiselect' && (
                            <div>
                                {opts.map((o, i) => (
                                    <Form.Check
                                        key={i}
                                        type="checkbox"
                                        id={`q_${f.id}_${i}`}
                                        label={o}
                                        checked={Array.isArray(answers[f.fieldKey]) && answers[f.fieldKey].includes(o)}
                                        onChange={() => toggleMulti(f.fieldKey, o)}
                                    />
                                ))}
                            </div>
                        )}
                        {f.inputType === 'number' && (
                            <Form.Control type="number" value={answers[f.fieldKey] || ''} onChange={(e) => setAnswer(f.fieldKey, e.target.value)} placeholder={f.helpText || ''} />
                        )}
                        {(!f.inputType || f.inputType === 'text') && (
                            <Form.Control type="text" value={answers[f.fieldKey] || ''} onChange={(e) => setAnswer(f.fieldKey, e.target.value)} placeholder={f.helpText || ''} />
                        )}
                        {f.helpText && f.inputType !== 'boolean' && <small className={bt.commonSize12}>{f.helpText}</small>}
                    </Form.Group>
                );
            })}
        </div>
    );
}

export default withStyles(bt)(QuestionnaireFields);
